/**
 * A push from the panel's composers (event announcement, «Send varsel til de påmeldte», «Send varsel til utvalget»).
 * Owner 2026-11-01: "notifications … should be normal and send by admins". Somebody with `push.approve` sends their
 * own push at once: the server stores it approved by them (`solo`) and the composer sends it right away with /send —
 * quiet hours and the other send rules answer there, as for any push. Somebody with only `push.compose` still sends it
 * to approval, and a janazah always waits for a second person (server and core access.pushNeedsSecondPerson).
 */
import { can, errText, post, type Body } from './api';

/** Whether the signed-in person sends their own (ordinary) push. */
export const sendsOwnPush = (): boolean => can('push.approve');

export const pushButtonLabel = (): string => (sendsOwnPush() ? 'Send varsel' : 'Send til godkjenning');

export const pushRuleNote = (): string => (sendsOwnPush()
  ? 'Varselet går ut med en gang du trykker «Send varsel». Det kan ikke trekkes tilbake.'
  : 'Varselet sendes til godkjenning; en annen ansatt godkjenner det under Varsler.');

/** What a successful /send says: whom it reached, in numbers (core rule 27). */
export function sentText(b: Body): string {
  return b && b.reach ? `Varselet er sendt til ${String(b.reach).charAt(0).toLowerCase()}${String(b.reach).slice(1)}.` : 'Varselet er sendt.';
}

export interface PushOutcome { ok: boolean; sent: boolean; text: string; tone: 'ok' | 'err' }

/**
 * Create the push and, when it is the author's own to send, send it. `ok` is whether the push exists now (the form can
 * be cleared); `sent` whether it went out. A push made but held — quiet hours, a failed delivery — says so and stays
 * under Varsler with «Send varsel».
 */
export async function submitPush(body: Body): Promise<PushOutcome> {
  const r = await post('/push', body);
  if (!r.ok) return { ok: false, sent: false, text: errText(r.body), tone: 'err' };
  if (!r.body.solo) {
    return { ok: true, sent: false, text: 'Sendt til godkjenning. Ingenting går ut før en annen har godkjent det under Varsler.', tone: 'ok' };
  }
  const s = await post('/push/' + encodeURIComponent(r.body.id) + '/send');
  if (s.ok) return { ok: true, sent: true, text: sentText(s.body), tone: 'ok' };
  const why = s.body.message_no || s.body.message || errText(s.body);
  return { ok: true, sent: false, tone: 'err',
    text: [why, s.body.stillApproved_no || 'Varselet er lagret, men ikke sendt. Send det under Varsler.'].filter(Boolean).join(' ') };
}
